// src/components/kuestion/campus/CampusApp.jsx
//
// Mounted under the Kuestion mount point. The gate reads the account mode first (v3 §1.1):
//   null    → the one-time Solo / Campus selection, at its own address
//             (choose-mode, Separation contract v2.1 §5.1)
//   solo    → the Solo app: dashboard, sittings, banks & templates, progress
//   campus  → the organisation wizard on first run, then the examiner shell
// account is the "mode already set" landing for either mode.
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '../shared/ThemeContext';
import { Loading, Alert } from '../shared/Bits';
import { CampusProvider, useCampus } from './CampusContext';
import ModeSelect from './ModeSelect';
import ModeLocked from './ModeLocked';
import CampusShell from './CampusShell';
import OrgWizard from './OrgWizard';
import Dashboard from './Dashboard';
import Students from './Students';
import AddStudent from './AddStudent';
import BulkImport from './BulkImport';
import Assignments from './Assignments';
import AssignmentNew from './AssignmentNew';
import CohortAnalytics from './CohortAnalytics';
import StudentReport from './StudentReport';
import Billing from './Billing';
import SoloShell from '../solo/SoloShell';
import SoloHome from '../solo/SoloHome';
import SoloExam from '../solo/SoloExam';
import SoloProgress from '../solo/SoloProgress';
import SoloBanks from '../solo/SoloBanks';
import SoloBilling from '../solo/SoloBilling';
import TemplateEditor from './TemplateEditor';
import ExamTemplates from '../pages/ExamTemplates';
import ManageData from '../pages/ManageData';
import { kq, kqRel } from '../../../config/routes';
import '../campus.css';

const Frame = ({ children }) => <div className="kqc"><div className="kqc-page kqc-page--narrow">{children}</div></div>;

const Gate = () => {
  const { modeState, modeError, reloadMode, orgState, orgError, reloadOrg } = useCampus();
  const location = useLocation();
  const onChooseMode = kqRel(location.pathname) === 'choose-mode';

  if (modeState === 'loading') return <Frame><Loading label="Opening Kuestion" /></Frame>;
  if (modeState === 'error') {
    return <Frame><Alert kind="error">{modeError}</Alert><button type="button" className="btn btn-primary" onClick={reloadMode}>Try again</button></Frame>;
  }
  if (modeState === 'choose') {
    return onChooseMode ? <ModeSelect /> : <Navigate to={kq('choose-mode')} replace />;
  }
  // The choice is permanent: once made, its address only leads back in.
  if (onChooseMode) return <Navigate to={kq()} replace />;

  if (modeState === 'solo') {
    return (
      <Routes>
        <Route path="account" element={<ModeLocked />} />
        <Route element={<SoloShell />}>
          <Route index element={<SoloHome />} />
          <Route path="exam" element={<SoloExam />} />
          <Route path="exam/:templateId" element={<SoloExam />} />
          <Route path="banks" element={<SoloBanks />} />
          <Route path="banks/manage" element={<div className="kqc-page"><ManageData /></div>} />
          <Route path="templates" element={<TemplateEditor />} />
          <Route path="templates/build" element={<div className="kqc-page"><ExamTemplates /></div>} />
          <Route path="progress" element={<SoloProgress />} />
          <Route path="billing" element={<SoloBilling />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Route>
      </Routes>
    );
  }

  // campus
  if (orgState === 'idle' || orgState === 'loading') return <Frame><Loading label="Opening Kuestion Campus" /></Frame>;
  if (orgState === 'error') {
    return <Frame><Alert kind="error">{orgError}</Alert><button type="button" className="btn btn-primary" onClick={reloadOrg}>Try again</button></Frame>;
  }
  if (orgState === 'none') return <OrgWizard />;

  return (
    <Routes>
      <Route path="account" element={<ModeLocked />} />
      <Route element={<CampusShell />}>
        <Route index element={<Dashboard />} />
        <Route path="students" element={<Students />} />
        <Route path="students/new" element={<AddStudent />} />
        <Route path="students/import" element={<BulkImport />} />
        <Route path="students/:id" element={<Students />} />
        <Route path="assignments" element={<Assignments />} />
        <Route path="assignments/new" element={<AssignmentNew />} />
        <Route path="analytics" element={<CohortAnalytics />} />
        <Route path="analytics/students/:id" element={<StudentReport />} />
        <Route path="templates" element={<TemplateEditor />} />
        <Route path="templates/build" element={<div className="kqc-page"><ExamTemplates /></div>} />
        <Route path="banks" element={<div className="kqc-page"><ManageData /></div>} />
        <Route path="billing" element={<Billing />} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Route>
    </Routes>
  );
};

export default function CampusApp() {
  return (
    <ThemeProvider>
      <CampusProvider>
        <Gate />
      </CampusProvider>
    </ThemeProvider>
  );
}
